import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { InView } from 'react-intersection-observer';
import isEmpty from 'lodash/isEmpty';
import { Row } from 'reactstrap';

import Meta from 'Components/Utility/SEO/Meta';
import { fetchSteamDeckGames } from 'Redux/ducks/algolia-calls';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import HitCard from 'Components/Cards/HitCard';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import Paginate from 'Components/Utility/Paginate';
import SteamDeckInformation from 'Components/Utility/SteamDeck/SteamDeckInformation';
import sortByMessages from 'Helpers/Messages/sort-by-messages';

const messages = defineMessages({
  title: {
    id: 'steam-deck.seotitle',
    defaultMessage: 'Steam Deck Games',
  },
  socialTitle: {
    id: 'steam-deck.socialtitle',
    defaultMessage: 'Steam Deck Games | Exclusive PC Steam Discounts',
  },
  desc: {
    id: 'steam-deck.seodesc',
    defaultMessage: 'Find the best prices on games for Steam Deck',
  },
});

const defaultHitsObject = { hits: [], nbHits: null, hitsPerPage: 36 };

const SteamDeckGames = ({
  location: { search },
  intl: { formatMessage },
}) => {
  const viewLatestDeals = getUrlParameter('view') === 'latest-deals';

  let defaultIndex = '';

  if (viewLatestDeals) {
    defaultIndex = 'latest_deals';
  }

  const [hitsObject, setHitsObject] = useState(defaultHitsObject);
  const [sort, setSort] = useState(defaultIndex);

  const [checkboxes, setCheckboxes] = useState({ verified: true, playable: true });

  const supportLevels = Object.keys(checkboxes).filter(s => checkboxes[s]).map(s => `steam_deck_support:${s}`).join(' OR ');

  const page = Number(getUrlParameter('page', search) || 1);

  useEffect(() => {
    const loadHits = async () => {
      setHitsObject(defaultHitsObject);
      const results = await fetchSteamDeckGames(supportLevels, sort, page - 1);
      return setHitsObject(results);
    };

    loadHits(sort);
  }, [sort, page, supportLevels]);

  const { hits, nbHits, hitsPerPage } = hitsObject;

  return (
    <div className="SteamDeckGames">
      <Meta
        brandTitle
        title={formatMessage(messages.title)}
        description={formatMessage(messages.desc)}
        socialTitle={formatMessage(messages.socialTitle)}
        url="steam-deck-games"
        hits={hits}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        pageType="CollectionPage"
      />

      <div className="pt-5 container SteamDeckGames__content">
        <div className="title-container">
          <InView>
            {({ inView, ref }) =>
              <div ref={ref}>
                <h1>
                  <FormattedMessage id="steam-deck.shorttitle" defaultMessage="Steam Deck Compatible Games" />
                </h1>
                {!inView &&
                  <div className="d-block d-sm-none">
                    <Backtotopbutton />
                  </div>
                }
              </div>
            }
          </InView>
        </div>

        <p className="seo-blurb-text mb-0">
          <FormattedMessage id="steam-deck.fulldesc" defaultMessage="Give your Steam Deck a boost with our top deals on Games for Steam Deck and PC. Featuring everything from top AAA titles to hidden Indie gems, check back regularly for our latest offers." />
        </p>

        <SteamDeckInformation banners />

        <nav data-nosnippet className={'categories-and-sortby-section d-flex pb-0'}>
          <div className="categories-container">
            <div className="mr-3"><FormattedMessage id="steam-deck.support" defaultMessage="Support" />:</div>
            <div className="d-flex dynamic-checkboxes-container">
              <div className="checkbox-container">
                <input
                  id="verified"
                  name="verified"
                  type="checkbox"
                  checked={checkboxes.verified}
                  disabled={checkboxes.verified && !checkboxes.playable}
                  aria-label="verified"
                  onChange={e => setCheckboxes({ ...checkboxes, verified: e.target.checked })}
                />
                <label htmlFor="verified" className="ml-1"><FormattedMessage id="steam-deck.verifiedcheckbox" defaultMessage="Verified" /></label>
              </div>
              <div className="checkbox-container">
                <input
                  id="playable"
                  name="playable"
                  type="checkbox"
                  checked={checkboxes.playable}
                  disabled={checkboxes.playable && !checkboxes.verified}
                  aria-label="playable"
                  onChange={e => setCheckboxes({ ...checkboxes, playable: e.target.checked })}
                />
                <label htmlFor="playable" className="ml-1"><FormattedMessage id="steam-deck.playablecheckbox" defaultMessage="Playable" /></label>
              </div>
            </div>
          </div>

          <div className="filters-container">
            <div className="mr-3"><FormattedMessage id="steam-deck.sortby" defaultMessage="Sort by" />:</div>
            <div className="dropdown-container sort-by">
              <select value={sort} onChange={e => setSort(e.target.value)}>
                <option value="">{formatMessage(sortByMessages.topSellers)}</option>
                <option value={'price_asc'}>{formatMessage(sortByMessages.priceLowHigh)}</option>
                <option value={'price_desc'}>{formatMessage(sortByMessages.priceHighLow)}</option>
                <option value={'name'}>{formatMessage(sortByMessages.alphabetical)}</option>
                <option value={'discount'}>{formatMessage(sortByMessages.topDiscount)}</option>
                <option value={'latest_deals'}>{formatMessage(sortByMessages.latestDeals)}</option>
                <option value={'ending_soon'}>{formatMessage(sortByMessages.endingSoon)}</option>
                <option value={'most_wanted'}>{formatMessage(sortByMessages.mostWanted)}</option>
                <option value={'release_date_desc'}>{formatMessage(sortByMessages.releaseDate)}</option>
                <option value={'top_rated'}>{formatMessage(sortByMessages.topRated)}</option>
              </select>
            </div>
          </div>
        </nav>

        {isEmpty(hits) &&
          <Row>
            <SkeletonHitCard
              numberOfCards={24}
              xsSize={6}
              mdSize={6}
              lgSize={4}
            />
          </Row>
        }

        {!isEmpty(hits) &&
          <React.Fragment>
            <ul className="HitCardsRow row">
              {hits.map((hit, i) =>
                <HitCard
                  lazy={i >= 6}
                  key={hit.product_id}
                  hit={hit}
                  xsSize={6}
                  mdSize={6}
                  lgSize={4}
                  hitCardContainerTag="li"
                  hitCardNameTag="h2"
                />,
              )}
            </ul>

            <Row>
              <Paginate hits={hitsObject} />
            </Row>
          </React.Fragment>
        }
      </div>
    </div>
  );
};

SteamDeckGames.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default injectIntl(SteamDeckGames);
